<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbs" />

    <!-- Stats Row -->
    <div class="jy-stats-row">
      <div v-for="stat in statsCards" :key="stat.label" class="jy-stat-card">
        <div class="jy-stat-left">
          <span class="jy-stat-label">{{ stat.label }}</span>
          <span class="jy-stat-value">{{ stat.value }}</span>
        </div>
        <div class="jy-stat-icon" :style="{ background: stat.bg, color: stat.color }">
          <i :class="stat.icon"></i>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="card mb-3">
      <div class="card-body py-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px">
          <div class="jy-search-box">
            <input
              type="text"
              class="form-control"
              v-model="searchQuery"
              placeholder="Buscar jornadas..."
              @input="debounceSearch"
            />
            <i class="bx bx-search-alt jy-search-icon"></i>
          </div>
          <button type="button" class="btn btn-outline-primary mr-2" @click="$router.push('/journeys-ai')">
            <i class="bx bx-bot mr-1"></i> Criar com IA
          </button>
          <button type="button" class="btn btn-success" @click="createNewJourney">
            <i class="bx bx-plus mr-1"></i> Nova Jornada
          </button>
        </div>
      </div>
    </div>

    <!-- Journeys Grid -->
    <div class="jy-grid">
      <div v-for="journey in journeys" :key="journey.id" class="jy-card" @click="openBuilder(journey.id)">
        <div class="jy-card-top">
          <div class="jy-card-left">
            <h5 class="jy-card-title">{{ journey.name }}</h5>
            <p class="jy-card-date">Criada em {{ formatDate(journey.created_at) }}</p>
          </div>
          <div class="d-flex align-items-center" style="gap: 8px">
            <span class="jy-badge" :class="'jy-badge-' + journey.status">
              {{ statusLabel(journey.status) }}
            </span>
            <b-dropdown variant="link" toggle-class="p-0 text-muted jy-menu-toggle" right no-caret>
              <template v-slot:button-content>
                <i class="bx bx-dots-vertical-rounded font-size-18"></i>
              </template>
              <b-dropdown-item @click.stop="openBuilder(journey.id)">
                <i class="bx bx-edit mr-1"></i> Editar
              </b-dropdown-item>
              <b-dropdown-item @click.stop="openDetailModal(journey.id, 'analytics')">
                <i class="bx bx-bar-chart-alt-2 mr-1"></i> Analytics
              </b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item @click.stop="duplicateJourney(journey)">
                <i class="bx bx-copy mr-1"></i> Duplicar
              </b-dropdown-item>
              <b-dropdown-item variant="danger" @click.stop="deleteJourney(journey)">
                <i class="bx bx-trash mr-1"></i> Excluir
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>

        <div class="jy-card-group" v-if="getTriggerGroup(journey)">
          <i class="bx bx-group"></i> {{ getTriggerGroup(journey) }}
        </div>

        <p class="jy-card-desc" v-if="journey.description">{{ journey.description }}</p>

        <!-- Stats -->
        <div class="jy-card-stats">
          <div class="jy-cs">
            <span class="jy-cs-val">{{ (journey.stats_cache && journey.stats_cache.total_enrolled) || 0 }}</span>
            <span class="jy-cs-label">Entraram</span>
          </div>
          <div class="jy-cs">
            <span class="jy-cs-val">{{ (journey.stats_cache && journey.stats_cache.active) || 0 }}</span>
            <span class="jy-cs-label">Ativos</span>
          </div>
          <div class="jy-cs">
            <span class="jy-cs-val">{{ (journey.stats_cache && journey.stats_cache.completed) || 0 }}</span>
            <span class="jy-cs-label">Completaram</span>
          </div>
          <div class="jy-cs">
            <span class="jy-cs-val">{{ (journey.stats_cache && journey.stats_cache.conversion_rate) || 0 }}%</span>
            <span class="jy-cs-label">Conversao</span>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!journeys || journeys.length === 0" class="jy-card-empty" @click="createNewJourney">
        <i class="bx bx-git-branch"></i>
        <h5>Criar nova jornada</h5>
        <p>Desenhe fluxos automatizados para seus participantes</p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="row mt-3" v-if="rows > perPage">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-right">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage" @input="updateList"></b-pagination>
          </ul>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
    <JourneyDetailModal
      v-model="showDetailModal"
      :journey-id="detailJourneyId"
      :initial-tab="detailTab"
    />
  </Layout>
</template>

<script>
import Swal from 'sweetalert2'
import { mapActions, mapState } from 'vuex'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import JourneyDetailModal from './components/modals/JourneyDetailModal.vue'
import { getGroups } from './services'

export default {
  components: {
    Layout,
    PageHeader,
    JourneyDetailModal
  },
  data () {
    return {
      title: 'Jornadas',
      breadcrumbs: [
        { text: 'Comunicacao', href: '/' },
        { text: 'Jornadas', active: true }
      ],
      searchQuery: '',
      searchTimeout: null,
      currentPage: 1,
      perPage: 12,
      showDetailModal: false,
      detailJourneyId: null,
      detailTab: 'analytics',
      groups: []
    }
  },
  mounted () {
    this.updateList()
    this.loadGroups()
  },
  methods: {
    ...mapActions('journeyBuilder', {
      getJourneys: 'GetJourneys',
      deleteAction: 'DeleteJourney',
      duplicateAction: 'DuplicateJourney'
    }),
    updateList () {
      let params = {
        limit: this.perPage,
        page: this.currentPage,
        search: this.searchQuery || undefined
      }
      let loader = this.$loading.show()
      this.getJourneys(params).then(() => {
        loader.hide()
      }).catch(error => {
        console.log(error)
        loader.hide()
      })
    },
    debounceSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.updateList()
      }, 400)
    },
    openBuilder (id) {
      this.$router.push(`/journeys/${id}`)
    },
    createNewJourney () {
      this.$router.push('/journeys/new')
    },
    openDetailModal (journeyId, tab) {
      this.detailJourneyId = journeyId
      this.detailTab = tab || 'analytics'
      this.showDetailModal = true
    },
    async duplicateJourney (journey) {
      try {
        let loader = this.$loading.show()
        await this.duplicateAction(journey.id)
        loader.hide()
        successMessage('Jornada duplicada com sucesso.', 2500)
        this.updateList()
      } catch (err) {
        errorMessage('Erro ao duplicar: ' + (err.message || err))
      }
    },
    deleteJourney (journey) {
      Swal.fire({
        title: 'Voce tem certeza?',
        html: `Sera excluida a jornada: <br /><b>${journey.name}</b>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, DESEJO EXCLUIR!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.deleteAction(journey.id)
            successMessage('Jornada excluida com sucesso.', 2500)
            this.updateList()
          } catch (err) {
            errorMessage('Alerta: ' + (err.message || err))
          }
        }
      })
    },
    async loadGroups () {
      try {
        const res = await getGroups()
        this.groups = res.data.data || res.data || []
      } catch (e) {
        this.groups = []
      }
    },
    getTriggerGroup (journey) {
      if (!journey.nodes || !journey.nodes.length) return null
      const trigger = journey.nodes.find(n => n.kind === 'registration' || n.kind === 'checkin')
      if (!trigger) return null
      const config = trigger.config && !Array.isArray(trigger.config) ? trigger.config : {}
      const ids = config.group_ids || (config.group_id ? [config.group_id] : [])
      if (!ids.length) return null
      const names = ids.map(id => {
        const g = this.groups.find(g => String(g.id) === String(id))
        return g ? g.name : null
      }).filter(Boolean)
      return names.length ? names.join(', ') : null
    },
    statusLabel (status) {
      const labels = { draft: 'Rascunho', active: 'Ativa', paused: 'Pausada', archived: 'Arquivada' }
      return labels[status] || status
    },
    formatDate (date) {
      if (!date) return '-'
      return this.$moment(date).format('DD/MM/YYYY')
    }
  },
  computed: {
    ...mapState('journeyBuilder', ['journeys', 'pagination']),
    rows () {
      return this.pagination.total || 0
    },
    statsCards () {
      const all = this.journeys || []
      return [
        { label: 'Jornadas Ativas', value: all.filter(j => j.status === 'active').length, icon: 'bx bx-play-circle', bg: '#ecfdf5', color: '#10b981' },
        { label: 'Total Entradas', value: all.reduce((sum, j) => sum + ((j.stats_cache && j.stats_cache.total_enrolled) || 0), 0), icon: 'bx bx-user-plus', bg: '#eff6ff', color: '#3b82f6' },
        { label: 'Conversao Media', value: this.avgConversion + '%', icon: 'bx bx-trending-up', bg: '#f5f3ff', color: '#8b5cf6' },
        { label: 'Total Jornadas', value: this.rows || all.length, icon: 'bx bx-git-branch', bg: '#fffbeb', color: '#f59e0b' }
      ]
    },
    avgConversion () {
      const all = this.journeys || []
      if (all.length === 0) return 0
      const total = all.reduce((sum, j) => sum + ((j.stats_cache && j.stats_cache.conversion_rate) || 0), 0)
      return Math.round(total / all.length)
    }
  }
}
</script>

<style scoped>
/* ── Stats Row ── */
.jy-stats-row {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}
.jy-stat-card {
  flex: 1;
  background: #fff;
  border: 1px solid #e2e5eb;
  border-radius: 5px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
}
.jy-stat-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}
.jy-stat-left {
  display: flex;
  flex-direction: column;
}
.jy-stat-label {
  font-size: 13px;
  font-weight: 500;
  color: #5b6478;
  margin-bottom: 2px;
}
.jy-stat-value {
  font-size: 22px;
  font-weight: 800;
  color: #1a1d26;
  letter-spacing: -0.5px;
  line-height: 1.2;
}
.jy-stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

/* ── Toolbar search ── */
.jy-search-box {
  flex: 1;
  position: relative;
}
.jy-search-box .form-control {
  padding-right: 36px;
}
.jy-search-icon {
  position: absolute;
  right: 13px;
  top: 0;
  line-height: 38px;
  font-size: 16px;
  color: #9aa1b2;
  pointer-events: none;
}

/* ── Journey Grid ── */
.jy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
}

/* ── Journey Card ── */
.jy-card {
  background: #fff;
  border: 1px solid #e2e5eb;
  border-radius: 5px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}
.jy-card:hover {
  border-color: #cbd1db;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.jy-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}
.jy-card-left {
  flex: 1;
  min-width: 0;
}
.jy-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #15181e;
  margin: 0 0 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.jy-card-date {
  font-size: 12px;
  color: #9aa1b2;
  margin: 0;
}
/* Group tag */
.jy-card-group {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: #3b82f6;
  background: #eff6ff;
  padding: 3px 10px;
  border-radius: 5px;
  margin-bottom: 10px;
}
.jy-card-group i {
  font-size: 14px;
}

.jy-card-desc {
  font-size: 12.5px;
  color: #9aa1b2;
  margin: 0 0 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Badge */
.jy-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.jy-badge-active { background: #ecfdf5; color: #059669; }
.jy-badge-draft { background: #f1f3f6; color: #9aa1b2; }
.jy-badge-paused { background: #fffbeb; color: #d97706; }
.jy-badge-archived { background: #f1f3f6; color: #5b6478; }

/* Menu toggle */
.jy-menu-toggle {
  opacity: 0;
  transition: opacity 0.15s;
}
.jy-card:hover .jy-menu-toggle {
  opacity: 1;
}

/* Card Stats */
.jy-card-stats {
  display: flex;
  padding-top: 14px;
  margin-top: 14px;
  border-top: 1px solid #eceef2;
}
.jy-cs {
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: center;
}
.jy-cs-val {
  font-size: 15px;
  font-weight: 700;
  color: #15181e;
  letter-spacing: -0.3px;
  line-height: 1.2;
}
.jy-cs-label {
  font-size: 11px;
  color: #9aa1b2;
  margin-top: 1px;
}

/* ── Empty State ── */
.jy-card-empty {
  background: #fff;
  border: 2px dashed #e2e5eb;
  border-radius: 5px;
  padding: 48px 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  grid-column: 1 / -1;
}
.jy-card-empty:hover {
  border-color: #34c38f;
  background: #ecfdf5;
}
.jy-card-empty i {
  font-size: 36px;
  color: #9aa1b2;
  margin-bottom: 10px;
  display: block;
}
.jy-card-empty h5 {
  font-size: 14px;
  font-weight: 700;
  color: #15181e;
  margin: 0 0 4px;
}
.jy-card-empty p {
  font-size: 12.5px;
  color: #9aa1b2;
  margin: 0;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .jy-stats-row {
    flex-wrap: wrap;
  }
  .jy-stat-card {
    flex: 1 1 calc(50% - 6px);
    min-width: 0;
  }
  .jy-grid {
    grid-template-columns: 1fr;
  }
}
</style>
